/**
 * Task System Frontend Logger
 * Provides comprehensive logging for debugging client issues
 */

class TaskSystemLogger {
    constructor() {
        this.sessionId = this.generateSessionId();
        this.requestId = null;
        this.userId = null;
        this.logs = [];
        this.maxLogs = 1000; // Maximum logs to keep in memory
        this.enableConsoleOutput = false; // Disabled - only store logs, don't output to console
        this.errorBuffer = []; // Buffer to store logs before an error
        this.bufferSize = 50; // Number of logs to keep before error
        this.autoSendOnError = true; // Automatically send logs on error
        this.enabled = true; // Master switch to enable/disable logging
        
        // Check if we should disable logging based on the current page
        this.checkIfShouldDisable();
        
        // Only setup handlers if logging is enabled
        if (this.enabled) {
            // Initialize error handlers
            this.setupErrorHandlers();
            
            // Setup performance observer
            this.setupPerformanceObserver();
        }
    }
    
    /**
     * Check if logging should be disabled based on current context
     */
    checkIfShouldDisable() {
        const currentPath = window.location.pathname;
        
        // Disable on auth/login pages
        if (currentPath.includes('/login') || 
            currentPath.includes('/auth') ||
            currentPath.includes('/register') ||
            currentPath.includes('/forgot-password') ||
            currentPath === '/' ||
            currentPath === '') {
            this.enabled = false;
        }
        
        // Disable if no authentication token
        const hasAuth = !!(localStorage.getItem('token') || localStorage.getItem('locationId'));
        if (!hasAuth) {
            this.enabled = false;
        }
    }

    /**
     * Generate unique session ID
     */
    generateSessionId() {
        return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Generate unique request ID
     */
    generateRequestId() {
        return 'req_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Set user context
     */
    setUser(userId, userEmail) {
        this.userId = userId;
        this.userEmail = userEmail;
    }

    /**
     * Log an action with context
     */
    log(level, action, data = {}) {
        // Skip if logging is disabled
        if (!this.enabled) {
            return null;
        }
        
        const logEntry = {
            timestamp: new Date().toISOString(),
            level,
            action,
            data,
            sessionId: this.sessionId,
            requestId: this.requestId,
            userId: this.userId,
            userAgent: navigator.userAgent,
            url: window.location.href,
            ...this.getDeviceInfo()
        };

        // Add to internal log buffer
        this.logs.push(logEntry);
        if (this.logs.length > this.maxLogs) {
            this.logs.shift(); // Remove oldest log
        }
        
        // Add to error buffer (circular buffer)
        this.errorBuffer.push(logEntry);
        if (this.errorBuffer.length > this.bufferSize) {
            this.errorBuffer.shift();
        }

        // Console output in development
        if (this.enableConsoleOutput) {
            console.log(`[TaskSystem ${level.toUpperCase()}]`, action, data);
        }

        // When error occurs, send all recent logs for context
        if ((level === 'error' || level === 'critical') && this.autoSendOnError) {
            // Include recent logs before the error for context
            const logsToSend = [...this.errorBuffer];
            this.sendLogsToServer(logsToSend, true);
            
            // Clear error buffer after sending
            this.errorBuffer = [];
        }

        return logEntry;
    }

    /**
     * Convenience methods for different log levels
     */
    debug(action, data) {
        return this.log('debug', action, data);
    }

    info(action, data) {
        return this.log('info', action, data);
    }

    warn(action, data) {
        return this.log('warning', action, data);
    }

    error(action, data) {
        return this.log('error', action, data);
    }

    /**
     * Log API request
     */
    logApiRequest(method, url, data = {}) {
        this.requestId = this.generateRequestId();
        return this.info('API_REQUEST', {
            method,
            url,
            requestData: this.sanitizeData(data),
            requestId: this.requestId
        });
    }

    /**
     * Log API response
     */
    logApiResponse(method, url, status, data = {}, duration = 0) {
        const logData = {
            method,
            url,
            status,
            duration: Math.round(duration),
            requestId: this.requestId
        };

        // Only include response data if it's not too large
        if (JSON.stringify(data).length < 5000) {
            logData.responseData = data;
        } else {
            logData.responseSize = JSON.stringify(data).length;
        }

        const level = status >= 400 ? 'error' : 'info';
        return this.log(level, 'API_RESPONSE', logData);
    }

    /**
     * Log user action
     */
    logUserAction(action, data = {}) {
        return this.info(`USER_ACTION_${action}`, data);
    }

    /**
     * Log component lifecycle
     */
    logComponent(component, event, data = {}) {
        return this.debug(`COMPONENT_${event}`, {
            component,
            ...data
        });
    }

    /**
     * Log performance metrics
     */
    logPerformance(operation, duration, data = {}) {
        return this.info('PERFORMANCE', {
            operation,
            duration: Math.round(duration),
            ...data
        });
    }

    /**
     * Setup global error handlers
     */
    setupErrorHandlers() {
        // Catch unhandled errors
        window.addEventListener('error', (event) => {
            // Log the error with full context
            this.error('UNHANDLED_ERROR', {
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                stack: event.error?.stack,
                userAgent: navigator.userAgent,
                url: window.location.href,
                timestamp: new Date().toISOString()
            });
            
            // Prevent default error handling in production
            if (process.env.NODE_ENV === 'production') {
                event.preventDefault();
            }
        });

        // Catch unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.error('UNHANDLED_REJECTION', {
                reason: event.reason?.message || event.reason,
                promise: event.promise,
                stack: event.reason?.stack,
                userAgent: navigator.userAgent,
                url: window.location.href,
                timestamp: new Date().toISOString()
            });
            
            // Prevent default in production
            if (process.env.NODE_ENV === 'production') {
                event.preventDefault();
            }
        });

        // Vue error handler (if Vue is available)
        if (window.Vue) {
            const originalErrorHandler = window.Vue.config.errorHandler;
            window.Vue.config.errorHandler = (err, vm, info) => {
                this.error('VUE_ERROR', {
                    message: err.message,
                    stack: err.stack,
                    info,
                    component: vm?.$options.name || 'Unknown'
                });
                
                // Call original handler if exists
                if (originalErrorHandler) {
                    originalErrorHandler(err, vm, info);
                }
            };
        }
    }

    /**
     * Setup performance observer
     */
    setupPerformanceObserver() {
        if ('PerformanceObserver' in window) {
            // Observe long tasks
            try {
                const observer = new PerformanceObserver((list) => {
                    for (const entry of list.getEntries()) {
                        if (entry.duration > 50) { // Log tasks longer than 50ms
                            this.warn('LONG_TASK', {
                                duration: Math.round(entry.duration),
                                startTime: Math.round(entry.startTime),
                                name: entry.name
                            });
                        }
                    }
                });
                observer.observe({ entryTypes: ['longtask'] });
            } catch (e) {
                // Some browsers don't support longtask
            }
        }
    }

    /**
     * Get device information
     */
    getDeviceInfo() {
        return {
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            colorDepth: window.screen.colorDepth,
            platform: navigator.platform,
            language: navigator.language,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled
        };
    }

    /**
     * Sanitize sensitive data
     */
    sanitizeData(data) {
        const sensitiveFields = ['password', 'token', 'api_key', 'secret', 'credit_card'];
        const sanitized = JSON.parse(JSON.stringify(data));

        const sanitizeObject = (obj) => {
            for (const key in obj) {
                if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
                    obj[key] = '***REDACTED***';
                } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                    sanitizeObject(obj[key]);
                }
            }
        };

        sanitizeObject(sanitized);
        return sanitized;
    }

    /**
     * Send logs to server
     * @param {Array} logsToSend - Specific logs to send
     * @param {boolean} isError - Whether this is being sent due to an error
     */
    async sendLogsToServer(logsToSend = null, isError = false) {
        // Only send logs if we're on a task system route and authenticated
        const currentPath = window.location.pathname;
        const isTaskRoute = currentPath.includes('/task') || 
                           currentPath.includes('/projects') || 
                           currentPath.includes('/kanban');
        
        // Check if user is authenticated (check for auth token or locationId)
        const isAuthenticated = !!(localStorage.getItem('token') || localStorage.getItem('locationId'));
        
        // Don't send logs from login/auth pages or when not authenticated
        if (!isTaskRoute || !isAuthenticated) {
            return;
        }
        
        const logs = logsToSend || this.logs.splice(0, this.logs.length);
        
        if (logs.length === 0) return;
        
        // Add metadata about why logs are being sent
        const payload = {
            logs,
            metadata: {
                isError,
                sessionId: this.sessionId,
                timestamp: new Date().toISOString(),
                logsCount: logs.length,
                totalSessionLogs: this.logs.length
            }
        };

        try {
            // Use axios with full URL to avoid interceptor transformation issues
            const response = await window.axios.post(`${window.location.origin}/api/task/logs`, payload);

            // Axios doesn't have response.ok, successful responses get here
        } catch (error) {
            // Silently handle the error - don't log to console
            // Re-add logs to buffer if sending failed
            if (!logsToSend) {
                this.logs.unshift(...logs);
            }
        }
    }

    /**
     * Download logs as JSON file
     */
    downloadLogs() {
        const data = {
            sessionId: this.sessionId,
            userId: this.userId,
            userEmail: this.userEmail,
            exportedAt: new Date().toISOString(),
            logs: this.logs
        };

        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `task-system-logs-${this.sessionId}-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }

    /**
     * Get current logs
     */
    getLogs(filter = {}) {
        let filteredLogs = [...this.logs];

        if (filter.level) {
            filteredLogs = filteredLogs.filter(log => log.level === filter.level);
        }

        if (filter.action) {
            filteredLogs = filteredLogs.filter(log => log.action.includes(filter.action));
        }

        if (filter.startTime) {
            filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) >= new Date(filter.startTime));
        }

        if (filter.endTime) {
            filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) <= new Date(filter.endTime));
        }

        return filteredLogs;
    }

    /**
     * Clear logs
     */
    clearLogs() {
        this.logs = [];
    }

    /**
     * Enable/disable console output
     */
    setConsoleOutput(enabled) {
        this.enableConsoleOutput = enabled;
    }
    
    /**
     * Enable/disable automatic log sending on error
     */
    setAutoSendOnError(enabled) {
        this.autoSendOnError = enabled;
    }
    
    /**
     * Set buffer size for error context
     */
    setBufferSize(size) {
        this.bufferSize = size;
        // Trim existing buffer if needed
        while (this.errorBuffer.length > size) {
            this.errorBuffer.shift();
        }
    }
    
    /**
     * Manually trigger sending of error buffer
     */
    sendErrorBuffer() {
        if (this.errorBuffer.length > 0) {
            this.sendLogsToServer([...this.errorBuffer], true);
            this.errorBuffer = [];
        }
    }
    
    /**
     * Enable or disable logging
     */
    setEnabled(enabled) {
        this.enabled = enabled;
        
        // If re-enabling, setup handlers if they weren't setup before
        if (enabled && !this._handlersSetup) {
            this.setupErrorHandlers();
            this.setupPerformanceObserver();
            this._handlersSetup = true;
        }
    }
    
    /**
     * Check if logging is enabled
     */
    isEnabled() {
        return this.enabled;
    }
}

// Create singleton instance
const taskSystemLogger = new TaskSystemLogger();

// Export for use in Vue/JavaScript
export default taskSystemLogger;

// Also attach to window for global access
if (typeof window !== 'undefined') {
    window.TaskSystemLogger = taskSystemLogger;
}